import "./WalletPage.scss";
import { useState, useEffect, useRef } from "react";
import { CreditCard, ArrowUpRight, Heart, Mic2, Headphones, MapPin } from "lucide-react";
import usePageTitle from "../../hooks/PageTitle";
import Brand from "../../components/Brand";
import { api } from "../../utils/api";
import { useCurrentUser } from "../../UserProvider";
import TransactionFlow from "./TransactionFlow";
import ArtistCard from "./ArtistCard";
import { trackEvent, initWalletAnalytics } from "../../utils/analytics";

// ─── Scroll-reveal hook ────────────────────────────────────────────────────────
function useReveal(margin = "-80px", sectionName = null) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          if (sectionName) trackEvent("wallet_section_viewed", { section_name: sectionName });
          obs.disconnect();
        }
      },
      { rootMargin: margin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [margin, sectionName]);
  return [ref, visible];
}

// ─── Data ──────────────────────────────────────────────────────────────────────
const HOW_IT_WORKS = [
  {
    Icon: CreditCard,
    number: "01",
    title: "Spend normally",
    description: "Link your card and keep living your life. Nothing changes about how you shop, eat, or commute.",
    detail: "$4.25 coffee → $5.00 rounded",
    mod: "hiw-card--blue",
  },
  {
    Icon: ArrowUpRight,
    number: "02",
    title: "We round up",
    description: "Every transaction rounds to the nearest dollar. $4.25 becomes $5.00. The extra cents are collected.",
    detail: "Average $0.50 per purchase",
    mod: "hiw-card--green",
  },
  {
    Icon: Heart,
    number: "03",
    title: "Artists create",
    description: "Your round-ups flow to the artists and causes you've chosen. They get 100%. No middleman fees.",
    detail: "100% to artists, always",
    mod: "hiw-card--red",
  },
];

const ARTISTS = [
  { name: "Rachel",                   role: "Musician",      description: "Singer-songwriter crafting intimate folk melodies in Brooklyn",                  image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop", percentage: 25, color: "rgba(234,24,41,0.12)"   },
  { name: "Sami",                     role: "Visual Artist", description: "Multidisciplinary artist exploring identity through paint and film",               image: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&h=400&fit=crop", percentage: 20, color: "rgba(245,158,11,0.12)" },
  { name: "Nomad Funk",               role: "Band",          description: "A four-piece bringing global grooves to underground stages",                       image: "https://images.unsplash.com/photo-1501386761578-eaa54b1cb5c7?w=400&h=400&fit=crop", percentage: 15, color: "rgba(139,92,246,0.12)"  },
  { name: "Mixolydian Commons",       role: "Nonprofit",     description: "A community music space committed to making music-making accessible to all",       image: "https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&h=400&fit=crop", percentage: 20, color: "rgba(34,197,94,0.12)"  },
  { name: "Women in Music",           role: "Nonprofit",     description: "Advancing equality and opportunity for women in the music industry",               image: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=400&fit=crop", percentage: 12, color: "rgba(244,63,94,0.12)"  },
  { name: "Immigrant Musicians Fund", role: "Nonprofit",     description: "Supporting displaced musicians rebuilding their creative lives",                   image: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=400&fit=crop", percentage:  8, color: "rgba(14,165,233,0.12)" },
];

const IMPACTS = [
  { Icon: Mic2,       metric: "6", unit: "rehearsal hours", label: "You helped fund 6 rehearsal hours this month",            mod: "impact-card--green" },
  { Icon: Headphones, metric: "2", unit: "studio sessions",  label: "You supported 2 studio sessions for emerging artists",   mod: "impact-card--blue"  },
  { Icon: MapPin,     metric: "3", unit: "local shows",      label: "You contributed to 3 local shows this quarter",          mod: "impact-card--green" },
];

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function WalletPage() {
  usePageTitle("Papaya Wallet");
  useEffect(() => { initWalletAnalytics(); }, []);
  const { currentUser } = useCurrentUser();
  const [email, setEmail] = useState("");
  const [submitState, setSubmitState] = useState("idle"); // idle | loading | success | error
  const submitting = useRef(false);
  const emailFocused = useRef(false);

  const [heroRef,   heroVisible]   = useReveal("0px",   "hero");
  const [hiwRef,    hiwVisible]    = useReveal("-60px", "how_it_works");
  const [mixRef,    mixVisible]    = useReveal("-60px", "support_mix");
  const [impactRef, impactVisible] = useReveal("-60px", "impact");
  const [footerRef, footerVisible] = useReveal("-60px", "signup");

  return (
    <div className="wlt">

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="wlt-hero">
        <div className="wlt-glow wlt-glow--hero-1" />
        <div className="wlt-glow wlt-glow--hero-2" />

        <div ref={heroRef} className={`wlt-hero__content wlt-fadein ${heroVisible ? "wlt-fadein--in" : ""}`}>
          <div className="wlt-badge">
            <span className="wlt-badge__dot" />
            <span className="wlt-badge__text">Now in early access</span>
          </div>

          <h1 className="wlt-hero__headline">
            Turn spare<br />
            change into{" "}
            <em>artist</em><br />
            <em>support</em>
          </h1>

          <p className="wlt-hero__sub">
            Every purchase you make rounds up a few cents. Those cents flow
            directly to musicians and creators you believe in.{" "}
            <strong>Effortlessly.</strong>
          </p>
        </div>

        <TransactionFlow />

        <div className={`wlt-hero__cta wlt-fadein ${heroVisible ? "wlt-fadein--in" : ""}`}
          style={{ transitionDelay: "400ms" }}>
          <a
            href="#wallet-signup"
            className="wlt-btn wlt-btn--primary"
            onClick={e => {
              e.preventDefault();
              trackEvent("wallet_hero_cta_click");
              document.getElementById("wallet-signup")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Start supporting artists
          </a>
          <span className="wlt-hero__cta-note">No fees. Cancel anytime. Artists get 100%.</span>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────────── */}
      <section className="wlt-section wlt-hiw">
        <div className="wlt-glow wlt-glow--hiw-left" />
        <div className="wlt-glow wlt-glow--hiw-right" />
        <div className="wlt-section__inner">
          <div ref={hiwRef} className={`wlt-section__heading wlt-fadein ${hiwVisible ? "wlt-fadein--in" : ""}`}>
            <span className="wlt-eyebrow">How it works</span>
            <h2 className="wlt-display">Effortless <em>patronage</em></h2>
          </div>

          <div className="wlt-hiw__grid">
            {HOW_IT_WORKS.map((s, i) => {
              const { Icon } = s;
              return (
                <div
                  key={s.number}
                  className={`hiw-card ${s.mod} wlt-fadein ${hiwVisible ? "wlt-fadein--in" : ""}`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  <div className="hiw-card__glow-overlay" />
                  <div className="hiw-card__icon-wrap"><Icon size={24} /></div>
                  <span className="hiw-card__num">Step {s.number}</span>
                  <h3 className="hiw-card__title">{s.title}</h3>
                  <p className="hiw-card__desc">{s.description}</p>
                  <div className="hiw-card__pill">
                    <span className="hiw-card__pill-dot" />
                    <span className="hiw-card__pill-text">{s.detail}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`wlt-hiw__flow wlt-fadein ${hiwVisible ? "wlt-fadein--in" : ""}`}
            style={{ transitionDelay: "400ms" }}>
            <div className="wlt-hiw__flow-dot wlt-hiw__flow-dot--blue" />
            <div className="wlt-hiw__flow-line" />
            <div className="wlt-hiw__flow-dot wlt-hiw__flow-dot--green" />
            <div className="wlt-hiw__flow-line" />
            <div className="wlt-hiw__flow-dot wlt-hiw__flow-dot--red" />
          </div>
        </div>
      </section>

      {/* ── SUPPORT MIX ──────────────────────────────────────────────────────── */}
      <section className="wlt-section wlt-mix">
        <div className="wlt-glow wlt-glow--mix" />
        <div className="wlt-section__inner wlt-section__inner--narrow">
          <div ref={mixRef} className={`wlt-section__heading wlt-fadein ${mixVisible ? "wlt-fadein--in" : ""}`}>
            <span className="wlt-eyebrow">Your support mix</span>
            <h2 className="wlt-display">Choose who <em>you</em> believe in</h2>
            <p className="wlt-section__sub">
              Curate a personal mix of artists and causes. Your round-ups flow to them
              automatically — like a playlist, but for patronage.
            </p>
          </div>

          <div className="wlt-mix__list">
            {ARTISTS.map((a, i) => <ArtistCard key={a.name} artist={a} index={i} />)}
          </div>

          <p className={`wlt-mix__caption wlt-fadein ${mixVisible ? "wlt-fadein--in" : ""}`}
            style={{ transitionDelay: "500ms" }}>
            Drag to adjust your mix. Artists get 100% of your round-ups.
          </p>
        </div>
      </section>

      {/* ── IMPACT ───────────────────────────────────────────────────────────── */}
      <section className="wlt-section wlt-impact">
        <div className="wlt-glow wlt-glow--impact" />
        <div className="wlt-section__inner wlt-section__inner--narrow">
          <div ref={impactRef} className={`wlt-section__heading wlt-fadein ${impactVisible ? "wlt-fadein--in" : ""}`}>
            <span className="wlt-eyebrow">Your impact</span>
            <h2 className="wlt-display">Small change,<br /><em>real impact</em></h2>
            <p className="wlt-section__sub">
              Your spare cents add up. Here's what your everyday spending made possible this month.
            </p>
          </div>

          <div className="wlt-impact__list">
            {IMPACTS.map((item, i) => {
              const { Icon } = item;
              return (
                <div
                  key={item.label}
                  className={`impact-card ${item.mod} wlt-fadein ${impactVisible ? "wlt-fadein--in" : ""}`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  <div className="impact-card__icon-wrap"><Icon size={24} /></div>
                  <p className="impact-card__label">{item.label}</p>
                  <div className="impact-card__metric-wrap">
                    <span className="impact-card__metric">{item.metric}</span>
                    <p className="impact-card__unit">{item.unit}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`wlt-impact__total wlt-fadein ${impactVisible ? "wlt-fadein--in" : ""}`}
            style={{ transitionDelay: "400ms" }}>
            <p className="wlt-impact__total-label">This month's total</p>
            <p className="wlt-impact__total-amount">$18<span>.42</span></p>
            <p className="wlt-impact__total-sub">from 47 round-ups across your daily purchases</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER CTA ───────────────────────────────────────────────────────── */}
      <footer id="wallet-signup" className="wlt-footer">
        <div className="wlt-footer__top-rule" />
        <div className="wlt-glow wlt-glow--footer" />
        <div ref={footerRef} className={`wlt-footer__inner wlt-fadein ${footerVisible ? "wlt-fadein--in" : ""}`}>
          <h2 className="wlt-footer__headline">
            Your everyday spending<br />
            can quietly keep artists <em>creating</em>
          </h2>
          <p className="wlt-footer__sub">
            Join the waitlist and be among the first to turn spare change into real support.
          </p>
          <form
            className="wlt-footer__form"
            onSubmit={async e => {
              e.preventDefault();
              if (submitting.current || (!currentUser && !email.trim()) || submitState === "success") return;
              submitting.current = true;
              setSubmitState("loading");
              trackEvent("wallet_signup_attempt", { form_name: "wallet_early_access", source: "wallet_footer" });
              try {
                await api.post("/wallet/early-access", { email: currentUser?.email || email });
                setSubmitState("success");
                setEmail("");
                trackEvent("wallet_signup_success", { form_name: "wallet_early_access", source: "wallet_footer" });
              } catch (err) {
                setSubmitState("error");
                trackEvent("wallet_signup_error", {
                  form_name: "wallet_early_access",
                  source: "wallet_footer",
                  error_category: err?.response?.status ? "api_error" : "network_error",
                });
                submitting.current = false;
              }
            }}
          >
            {currentUser ? (
              <p className="wlt-footer__signed-in-note">Signed in as {currentUser.email}</p>
            ) : (
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="wlt-footer__input"
                onFocus={() => {
                  if (emailFocused.current) return;
                  emailFocused.current = true;
                  trackEvent("wallet_email_focus", { form_name: "wallet_early_access" });
                }}
              />
            )}
            <button
              type="submit"
              className="wlt-btn wlt-btn--primary wlt-footer__submit"
              disabled={submitState === "loading" || submitState === "success"}
            >
              {submitState === "loading" ? "Sending…" : submitState === "success" ? "You're on the list ✓" : "Get early access"}
            </button>
          </form>
          {submitState === "error" && (
            <p className="wlt-footer__form-error">Something went wrong — please try again.</p>
          )}

          <div className="wlt-footer__bottom">
            <div className="wlt-footer__brand">
              <Brand.Logo height={32} className="wlt-footer__brand-logo" />
            </div>
            <p className="wlt-footer__copy">
              © {new Date().getFullYear()} Papaya. Built for artists, by people who care.
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
}
